import { Button, Flex, IconLoading, Modal, Text } from "@powerledger/ui-component-lib";
import { Formik } from "formik";
import { kebabCase } from "lodash";
import { useTranslation } from "react-i18next";
import { date, object } from "yup";

import { LocalDatePicker } from "../date-picker";
import { Form, FormFieldLabel } from "../form";
import {
  ForwardTradeChangeRequestModalTitle,
  ForwardTradeChangeRequestModalViewProps,
} from "./forward-trade-change-request-modal.types";

export const ForwardTradeChangeRequestModalView = ({
  visible,
  closeModal,
  initialData,
  onSubmit,
  mode,
}: ForwardTradeChangeRequestModalViewProps) => {
  const { t } = useTranslation();

  return (
    <Modal visible={visible} onCancel={closeModal} modalMaxWidth="800px" containerOverflow="visible">
      <Flex sx={{ flexDirection: "column", gap: 2 }} data-testid={kebabCase(ForwardTradeChangeRequestModalTitle[mode])}>
        <Flex sx={{ justifyContent: "space-between" }}>
          <Text sx={{ fontSize: 4, fontWeight: "bold" }}>{t(ForwardTradeChangeRequestModalTitle[mode])}</Text>
        </Flex>
        <Formik
          onSubmit={onSubmit}
          initialValues={{ paymentDate: initialData.paymentDate, deliveryDate: initialData.deliveryDate }}
          validationSchema={object().shape({
            paymentDate: date().nullable().required(t("Payment Date is required")),
            deliveryDate: date().nullable().required(t("Delivery Date is required")),
          })}
        >
          {({ values, handleSubmit, setFieldValue, setFieldTouched, isSubmitting }) => {
            const isDisabled =
              !values.paymentDate ||
              !values.deliveryDate ||
              (initialData.paymentDate === values.paymentDate && initialData.deliveryDate === values.deliveryDate);
            return (
              <Form onSubmit={handleSubmit}>
                <Flex sx={{ flexDirection: "column", gap: 2 }}>
                  <Flex sx={{ flexDirection: "row", gap: 2, justifyContent: "space-between" }}>
                    <Form.Item sx={{ width: 350 }}>
                      <FormFieldLabel label={t("Payment Date")} name="paymentDate">
                        <LocalDatePicker
                          id="payment-date-picker"
                          noOffset
                          value={values.paymentDate}
                          minDate={new Date()}
                          onBlur={() => setFieldTouched("paymentDate", true)}
                          onChange={(date) => {
                            setFieldValue("paymentDate", date);
                          }}
                        />
                      </FormFieldLabel>
                    </Form.Item>

                    <Form.Item sx={{ width: 350 }}>
                      <FormFieldLabel
                        label={t("Delivery Date (On or Before)")}
                        name="deliveryDate"
                        info={t(
                          "Delivery will execute as soon as the seller assigns the full contracted volume that meets all requirements, any time on or before this date.",
                        )}
                      >
                        <LocalDatePicker
                          id="delivery-date-picker"
                          noOffset
                          value={values.deliveryDate}
                          minDate={new Date()}
                          onBlur={() => setFieldTouched("deliveryDate", true)}
                          onChange={(date) => {
                            setFieldValue("deliveryDate", date);
                          }}
                        />
                      </FormFieldLabel>
                    </Form.Item>
                  </Flex>
                  <Flex sx={{ justifyContent: "flex-end" }}>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isDisabled || isSubmitting}
                      data-testid="submit-change-request"
                    >
                      {t("Submit")}
                    </Button>
                    {isSubmitting && <IconLoading sx={{ ml: 2 }} size="small" />}
                  </Flex>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      </Flex>
    </Modal>
  );
};
